// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    executeAwaitableHandlerChain,
    HandlerWithResult,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import {
    chain,
    LoadRealTimeInventory,
    ProcessProducts,
    RequestDataFromApi,
    UploadError,
} from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import {
    OrderUploadRowError,
    UploadedItem,
} from "@insite/client-framework/Store/Components/OrderUpload/OrderUploadState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { AvailabilityDto, ProductDto, RealTimeInventoryModel } from "@insite/client-framework/Types/ApiModels";
import { batchGetProducts, BatchGetProductsApiParameter } from "@mcmc/App/Services/ProductService";

export interface BatchLoadProductsParameter extends BatchGetProductsApiParameter {
    firstRowHeading: boolean;
    checkInventory: boolean;
    bypassConflicts: boolean;
}
export interface OwnProductDto extends ProductDto {
    index: number;
    key: string;
    product: any;
}
export interface BatchLoadProductsResult {
    apiParameter: BatchGetProductsApiParameter;
    apiResult: (OwnProductDto | null)[];
    products: ProductDto[];
    rowErrors: OrderUploadRowError[];
}

type HandlerType = HandlerWithResult<BatchLoadProductsParameter, BatchLoadProductsResult>;

export const CustomRequestDataFromApi: HandlerType = async props => {
    props.result.apiResult = await batchGetProducts(props.result.apiParameter);
};

export const CustomLoadRealTimeInventory: HandlerType = async ({
    result: { apiResult },
    dispatch,
    getState,
    parameter,
}) => {
    if (!parameter.checkInventory) {
        return;
    }
    const products = apiResult.filter(p => !!p?.product) as OwnProductDto[];
    const validProducts = products.map(p => p?.product);
    if (!validProducts.length) {
        return;
    }

    const realTimeInventory = await executeAwaitableHandlerChain<
        Parameters<typeof loadRealTimeInventory>[0],
        RealTimeInventoryModel
    >(
        loadRealTimeInventory,
        {
            productIds: validProducts.map(o => o.id),
            onComplete: realTimeInventoryProps => {
                if (realTimeInventoryProps.error) {
                    dispatch({
                        type: "Components/OrderUpload/SetFailedToLoadInventory",
                        parameter: { failedToLoadInventory: true },
                    });
                }
            },
        },
        { dispatch, getState },
    );

    realTimeInventory.realTimeInventoryResults?.forEach(inventory => {
        validProducts
            .filter(p => p.id === inventory.productId)
            .forEach(product => {
                product.qtyOnHand = inventory.qtyOnHand;

                const inventoryAvailability = inventory.inventoryAvailabilityDtos?.find(
                    o => o.unitOfMeasure === product.unitOfMeasure,
                );
                product.availability = inventoryAvailability?.availability || {
                    messageType: 0,
                    message: "",
                    requiresRealTimeInventory: false,
                };

                product.productUnitOfMeasures?.forEach(
                    (productUoM: {
                        unitOfMeasure: string;
                        availability:
                            | AvailabilityDto
                            | { messageType: number; message: string; requiresRealTimeInventory: boolean };
                    }) => {
                        const inventoryAvailability = inventory.inventoryAvailabilityDtos?.find(
                            o => o.unitOfMeasure === productUoM.unitOfMeasure,
                        );
                        productUoM.availability = inventoryAvailability?.availability || {
                            messageType: 0,
                            message: "",
                            requiresRealTimeInventory: false,
                        };
                    },
                );
            });
    });
};

export const CustomProcessProducts: HandlerType = props => {
    const state = props.getState();
    const currentPage = getCurrentPage(state);
    const processState =
        ["VmiBinsPage", "VmiLocationDetailsPage"].indexOf(currentPage.type) !== -1
            ? state.components.vmiBinsImportModal.modalIsOpen
                ? state.components.vmiBinsImportModal
                : state.components.vmiCountsImportModal
            : state.components.orderUpload;
    const productsGroups = props.result.apiResult as any;
    const groups = [];
    if ("uploadCancelled" in processState && processState.uploadCancelled) {
        return false;
    }

    for (let i = 0; i < productsGroups.length; i++) {
        const products = productsGroups[i].products as ProductDto[];
        const index = props.parameter.firstRowHeading ? i + 2 : i + 1;
        const item =
            "uploadedItems" in processState
                ? processState.uploadedItems[i]
                : "parsedItems" in processState
                ? processState.parsedItems[i]
                : null;

        if (item == null) {
            continue;
        }

        if (products === null) {
            props.result.rowErrors.push(
                mapRowError(index, UploadError.NotFound, item.name, {
                    qtyOrdered: item.qtyOrdered,
                    unitOfMeasureDisplay: item.unitOfMeasure,
                } as ProductDto),
            );
            continue;
        }

        if (products.length === 1 || (props.parameter.bypassConflicts && products.length > 1)) {
            const product = products[0];
            const error = validateProduct(product, props.parameter.checkInventory);
            if (error === UploadError.None) {
                product.qtyOrdered = !item.qtyOrdered ? 1 : item.qtyOrdered;
                const isInvalidUnitOfMeasure = setProductUnitOfMeasure(product, item, index, props.result.rowErrors);
                if (!isInvalidUnitOfMeasure) {
                    addProductToList(product, item, index, props.result, props.parameter.checkInventory);
                }
            } else {
                props.result.rowErrors.push(mapRowError(index, error, item.name, product));
            }
        }

        if (products.length > 1 && !props.parameter.bypassConflicts) {
            const group = productsGroups[i];
            group.item = item;
            groups.push(group);
        }
    }
    props.dispatch({
        type: "xOrderUpload/SetConflictsGroups",
        showConflicts: groups.length > 0 && props.result.rowErrors.length === 0,
        conflictsGroups: groups,
        checkInventory: props.parameter.checkInventory,
    } as any);
};

export const validateProduct = (product: ProductDto, checkInventory: boolean) => {
    if (product.canConfigure || (product.isConfigured && !product.isFixedConfiguration)) {
        return UploadError.ConfigurableProduct;
    }

    if (product.isStyleProductParent) {
        return UploadError.StyledProduct;
    }

    if (checkInventory && product.qtyOnHand === 0 && product.trackInventory && !product.canBackOrder) {
        return UploadError.OutOfStock;
    }

    if (!product.canAddToCart) {
        return UploadError.Unavailable;
    }

    return UploadError.None;
};

export const setProductUnitOfMeasure = (
    product: ProductDto,
    item: UploadedItem,
    index: number,
    rowErrors: OrderUploadRowError[],
) => {
    const uoms = getProductUnitOfMeasures(product, item);
    if (uoms.length > 0) {
        const um = uoms[0];
        product.selectedUnitOfMeasure = um.unitOfMeasure;
        product.selectedUnitOfMeasureDisplay = um.unitOfMeasureDisplay;
        product.unitOfMeasure = um.unitOfMeasure;
        product.unitOfMeasureDisplay = um.unitOfMeasureDisplay;

        return false;
    }

    if (item.unitOfMeasure) {
        const errorProduct = mapRowError(index, UploadError.InvalidUnit, item.name, product);
        errorProduct.umRequested = item.unitOfMeasure;
        rowErrors.push(errorProduct);

        return true;
    }

    return false;
};

export const addProductToList = (
    product: ProductDto,
    item: UploadedItem,
    index: number,
    result: BatchLoadProductsResult,
    checkInventory: boolean,
) => {
    const baseUnitOfMeasure = getBaseUnitOfMeasure(product);
    const currentUnitOfMeasure = getCurrentUnitOfMeasure(product);
    if (
        checkInventory &&
        product.trackInventory &&
        !product.canBackOrder &&
        !product.quoteRequired &&
        baseUnitOfMeasure &&
        currentUnitOfMeasure &&
        product.qtyOrdered * baseUnitOfMeasure.qtyPerBaseUnitOfMeasure >
            product.qtyOnHand * currentUnitOfMeasure.qtyPerBaseUnitOfMeasure
    ) {
        const rowError = mapRowError(index, UploadError.NotEnough, item.name, product);
        result.rowErrors.push(rowError);
    }

    result.products.push(product);
};

const getBaseUnitOfMeasure = (product: ProductDto) => {
    const defaultUnitOfMeasure = getDefaultProductUnitOfMeasureDto();
    let baseUnitOfMeasure = product.productUnitOfMeasures?.filter(u => u.isDefault)[0];
    if (!baseUnitOfMeasure) {
        baseUnitOfMeasure = defaultUnitOfMeasure;
    }

    return baseUnitOfMeasure;
};

const getCurrentUnitOfMeasure = (product: ProductDto) => {
    const defaultUnitOfMeasure = getDefaultProductUnitOfMeasureDto();
    let currentUnitOfMeasure = product.productUnitOfMeasures?.filter(u => u.unitOfMeasure === product.unitOfMeasure)[0];
    if (!currentUnitOfMeasure) {
        currentUnitOfMeasure = defaultUnitOfMeasure;
    }

    return currentUnitOfMeasure;
};

const getDefaultProductUnitOfMeasureDto = () => {
    return {
        productUnitOfMeasureId: "",
        unitOfMeasure: "",
        unitOfMeasureDisplay: "",
        description: "",
        qtyPerBaseUnitOfMeasure: 1,
        roundingRule: "",
        isDefault: false,
        availability: null,
    };
};

export const mapRowError = (
    index: number,
    error: UploadError,
    name: string,
    product: ProductDto,
): OrderUploadRowError => {
    return {
        index,
        error,
        name,
        qtyRequested: product.qtyOrdered,
        umRequested: product.unitOfMeasureDisplay,
        qtyOnHand: product.qtyOnHand,
    };
};

const getProductUnitOfMeasures = (product: ProductDto, item: UploadedItem) => {
    const lowerCaseItemUm = item.unitOfMeasure ? item.unitOfMeasure.toLowerCase() : "";

    return lowerCaseItemUm && product.productUnitOfMeasures
        ? product.productUnitOfMeasures.filter(
              u =>
                  u.unitOfMeasure.toLowerCase() === lowerCaseItemUm ||
                  u.unitOfMeasureDisplay.toLowerCase() === lowerCaseItemUm ||
                  u.description.toLowerCase() === lowerCaseItemUm,
          )
        : [];
};

replaceInChain(chain, RequestDataFromApi, CustomRequestDataFromApi); // <xng-change/>
replaceInChain(chain, LoadRealTimeInventory, CustomLoadRealTimeInventory); // <xng-change/>
replaceInChain(chain, ProcessProducts, CustomProcessProducts); // <xng-change/>
